import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import BaseModal from '@components/common/form/BaseModal';
import apiConfig from '@constants/apiConfig';
import { errorCodes } from '@constants/ErrorCode';
import useFetch from '@hooks/useFetch';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import ModalFormFooter from '@modules/kitplayShopAccount/device/DeviceTab/ModalFormFooter';
import { showErrorMessage } from '@services/notifyService';

import PrinterMappingForm from './PrinterMappingForm';

const message = defineMessages({
    objectName: 'Printer mapping',
    saveSuccess: 'Save printer mapping successfully',
});

const FORM_ID = 'form-devicetab-printer-mapping';
const EMPTY = {};

const usePrinterMappingFormModal = ({ onReloadRow, deviceId } = {}) => {
    const translate = useTranslate();
    const notification = useNotification();

    const [ open, setOpen ] = useState(false);
    const [ detail, setDetail ] = useState(null);
    const [ isEditing, setIsEditing ] = useState(false);
    const [ changed, setChanged ] = useState(false);
    const [ printerId, setPrinterId ] = useState(null);
    const [ ordering, setOrdering ] = useState(undefined);
    const [ reloadTarget, setReloadTarget ] = useState(null);

    const { execute: executeGetDetail } = useFetch(apiConfig.printerMapping.getById, { immediate: false });
    const { execute: executeCreate, loading: loadingCreate } = useFetch(apiConfig.printerMapping.create, {
        immediate: false,
    });
    const { execute: executeUpdate, loading: loadingUpdate } = useFetch(apiConfig.printerMapping.update, {
        immediate: false,
    });

    const handleSaveError = (err) => {
        const errorCode = err?.response?.data?.code ?? err?.code;
        const errorMessage = errorCodes[errorCode]?.message;
        if (errorMessage) showErrorMessage(translate.formatMessage(errorMessage), translate);
        else showErrorMessage(err?.message || translate.formatMessage(commonMessage.error), translate);
    };

    const closeModal = () => {
        setOpen(false);
        setDetail(null);
        setChanged(false);
        setPrinterId(null);
        setOrdering(undefined);
        setReloadTarget(null);
    };

    const openMappingCreate = (record, nextOrdering) => {
        setIsEditing(false);
        setDetail({});
        setChanged(false);
        setPrinterId(record?.printer?.id);
        setOrdering(nextOrdering);
        setReloadTarget(record);
        setOpen(true);
    };

    const openMappingEdit = (childItem) => {
        executeGetDetail({
            pathParams: { id: childItem.id },
            onCompleted: (res) => {
                setDetail(res?.result === true ? res.data : {});
                setIsEditing(true);
                setChanged(false);
                setPrinterId(childItem?.printer?.id);
                setReloadTarget(childItem);
                setOpen(true);
            },
            onError: () => notification({ type: 'error', message: translate.formatMessage(commonMessage.error) }),
        });
    };

    const onSuccess = () => {
        notification({ message: translate.formatMessage(message.saveSuccess) });
        const target = reloadTarget;
        closeModal();
        if (target) onReloadRow?.(target);
    };

    const handleSubmit = (values) => {
        if (isEditing) {
            executeUpdate({
                data: { ...values, id: detail?.id },
                onCompleted: (res) => res?.result === true && onSuccess(),
                onError: handleSaveError,
            });
        } else {
            executeCreate({
                data: { ...values, ordering: ordering != null ? Number(ordering) : undefined },
                onCompleted: (res) => res?.result === true && onSuccess(),
                onError: handleSaveError,
            });
        }
    };

    const renderModals = () =>
        (
            <BaseModal
                isModalVisible={open}
                isEditing={isEditing}
                handleCancel={closeModal}
                title={translate.formatMessage(message.objectName)}
                width="800px"
                noBodyPadding={true}
                customFooter={
                    <ModalFormFooter
                        formId={FORM_ID}
                        onCancel={closeModal}
                        loading={loadingCreate || loadingUpdate}
                        disabled={!changed}
                        isEditing={isEditing}
                    />
                }
            >
                {open && (
                    <PrinterMappingForm
                        formId={FORM_ID}
                        dataDetail={detail || EMPTY}
                        isEditing={isEditing}
                        printerId={printerId}
                        actions={null}
                        onSubmit={handleSubmit}
                        setIsChangedFormValues={setChanged}
                        deviceId={deviceId}
                    />
                )}    
            </BaseModal>
        );

    return { openMappingCreate, openMappingEdit, renderModals };
};

export default usePrinterMappingFormModal;
